import { ArenaOpsUsdtSchema } from "./arena-ops-filters.dto";
import { z } from "zod";

/**
 * M5 — entries per bucket by buy-in. Entry-level, so bucketed by the ENTRY's
 * own fee, not the fee its match settled at.
 *
 * Buckets are DERIVED from the fee, never enumerated: a rung carrying only a
 * handful of entries is still real, and a hard-coded ladder drops it.
 *
 * Directed rematches and ticket-funded entries are COUNTED and broken out, not
 * filtered. They are real demand at the entry level; the reason to exclude
 * rematches from the pairing panels — they cannot pair freely — does not apply
 * here, and hiding them would make these totals irreconcilable with route 27.
 */
export const ArenaOpsMatchmakingEntriesByBuyInPointSchema = z.object({
  bucketStart: z.iso.datetime(),
  /**
   * The bucket's identity: `free`, `$1`, `$5`, `$10`, `$20`, `$50`, `S&G $10`.
   * A composite of tier and amount, because those alone do not identify a
   * bucket — Spin & Go and the paid ladder both have a 10-USDT rung, and
   * keying on the amount merges S&G's entries into the `$10` rung.
   */
  buyInLabel: z.string(),
  /** `SPIN_AND_GO`, `PAID` or `FREE` — the label's tier half, split out. */
  tier: z.string(),
  /** The label's amount half, split out. Decimal-USDT string. */
  buyInUsdt: ArenaOpsUsdtSchema,
  entries: z.number().int(),
  /** Of `entries`, the ones that skipped the queue as a directed rematch. */
  ofWhichRematch: z.number().int(),
  /** Of `entries`, the ones a free ticket paid for (`fundedByTicketId`). */
  ofWhichTicket: z.number().int(),
  /** Buy-in value that actually reached payment — entries with no `paidAt` contribute 0. */
  stakedUsdt: ArenaOpsUsdtSchema,
});

/** One bucket rolled up over the WHOLE window — `points` summed on `buyInLabel`. */
export const ArenaOpsMatchmakingEntriesByBuyInTotalSchema = z.object({
  buyInLabel: z.string(),
  tier: z.string(),
  buyInUsdt: ArenaOpsUsdtSchema,
  entries: z.number().int(),
  ofWhichRematch: z.number().int(),
  ofWhichTicket: z.number().int(),
  stakedUsdt: ArenaOpsUsdtSchema,
});

export const ArenaOpsMatchmakingEntriesByBuyInResponseSchema = z.object({
  /** Every bucket label observed in the window, ascending by amount. Derived, never enumerated. */
  buyInLabels: z.array(z.string()),
  /**
   * Per-bucket rollup over the whole window, ascending by amount. Redundant
   * with `points` by construction — it is the same rows summed — and carried
   * because that is the grain `oracle.sql` reports at, so the two are directly
   * comparable without the caller re-aggregating and introducing its own bug.
   */
  totals: z.array(ArenaOpsMatchmakingEntriesByBuyInTotalSchema),
  /** Per time bucket per buy-in — the chart series. */
  points: z.array(ArenaOpsMatchmakingEntriesByBuyInPointSchema),
});
export type ArenaOpsMatchmakingEntriesByBuyInResponse = z.infer<typeof ArenaOpsMatchmakingEntriesByBuyInResponseSchema>;
